import { type Conversation } from "shared/apiClient";
import { AssessmentLights } from "shared/types";
import { getText, interpolate } from "shared/lib";

import { useCouncilLocalisation } from "shared/hooks";

import { ExternalLinkIcon } from "shared/components";

export interface BORProps {
  assessmentLights: string;
  conversation: Conversation | undefined | null;
}

export function BOR({ assessmentLights, conversation }: BORProps) {
  const { getLocalisationEntry } = useCouncilLocalisation(conversation);

  const renderPlanningHistory = () => {
    return (
      <div>
        <h3 className="text-headingmd">{getText("Planning history")}</h3>
        <p className="text-bodymd mb-2">
          {interpolate(
            "The City of Boroondara will contact you via {{email}} to confirm if there is an existing planning permit on the land relevant to your proposal. If there is a permit on the land, this might require updating the existing approval to comply with the conditions on that permit.",
            {
              email: (
                <a
                  className="text-textLink hover:text-textLinkHover cursor-pointer underline"
                  href="mailto:boroondara@boroondara.vic.gov.au"
                  rel="noreferrer"
                  tabIndex={0}
                >
                  boroondara@boroondara.vic.gov.au
                </a>
              ),
            },
          )}
        </p>
        <p className="text-bodymd font-semibold">
          {getText("Amend or extend an existing planning permit")}
        </p>
        <p className="text-bodymd mb-2">
          {getText(
            "Find out when and how to amend or extend a Planning Permit in Boroondara, and how we assess your application.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover inline-flex cursor-pointer underline"
          href="https://www.boroondara.vic.gov.au/services/planning-and-building/planning/planning-permits/amend-planning-permit"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Amend or extend a Planning Permit")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderApplyForWrittenPlanningAdvice = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Apply for written planning advice")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "Your builder may request confirmation from Council that a planning permit isn't required before they start work. To get this, a Council planner needs to review your proposal and provide written planning advice, which is the second step on our website.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover inline-flex cursor-pointer underline"
          href="https://www.boroondara.vic.gov.au/services/planning-and-building/planning/planning-permits/planning-permit-applications/prepare-your-planning-permit-application"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Request written planning advice")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderCheckIfYouNeedABuildingPermit = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Check if you need a building permit")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "Building permits are issued separately from planning permits and are required to ensure construction work complies with national building regulations.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover inline-flex cursor-pointer underline"
          href="https://www.boroondara.vic.gov.au/planning-building/building-construction/building-permits-and-approvals"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Check if you need a building permit")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderCheckForExistingPermits = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Check for existing planning permits")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "If the property has an existing planning permit, it may affect your proposal's approval pathway. Speak to Council's planning department to see if a planning permit exists, and if one does, whether your proposal will require an amendment to a permit.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href={getLocalisationEntry("councilContactLink")}
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Contact Council")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderRequestCertificateOfTitle = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Request a Certificate of Title")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "This tool doesn't account for covenants (restrictions), Section 173 Agreements, caveats and easements, which can impact what you can and can't do on a property. A Certificate of Title can include this information, which you can request online via LANDATA.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover inline-flex cursor-pointer underline"
          href={getLocalisationEntry("councilApplyForCertificateOfTitleLink")}
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Request a Certificate of Title")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderDiscussWithPlanners = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Discuss your proposal with a planner")}
        </h3>
        <p className="text-bodymd mb-2">
          {interpolate(
            " Have questions? {{link}} to speak to one of our planners. When you contact us, tell us your report ID:",
            {
              link: (
                <a
                  className="text-textLink hover:text-textLinkHover cursor-pointer underline"
                  href={getLocalisationEntry("councilContactLink")}
                  rel="noreferrer"
                  tabIndex={0}
                  target="_blank"
                >
                  {getText("Contact us")}
                </a>
              ),
            },
          )}
        </p>
        <div className="border-interactiveBorder bg-interfaceWhite text-textPrimary text-bodylg w-min text-nowrap rounded border px-2 py-1.5 font-medium">
          {conversation?.identifier?.toUpperCase() || ""}
        </div>
      </div>
    );
  };

  const renderDiscussProhibitedWithPlanners = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Discuss your proposal with a planner")}
        </h3>
        <p className="text-bodymd mb-2">
          {interpolate(
            "The proposal you discussed appears to not accord with some applicable {{mandatory}}. If you would like to discuss these controls, please {{link}} and tell us your report ID:",
            {
              mandatory: <b>{getText("mandatory planning controls")}</b>,
              link: (
                <a
                  className="text-textLink hover:text-textLinkHover cursor-pointer underline"
                  href={getLocalisationEntry("councilContactLink")}
                  rel="noreferrer"
                  tabIndex={0}
                  target="_blank"
                >
                  {getText("Contact us")}
                </a>
              ),
            },
          )}
        </p>
        <div className="border-interactiveBorder bg-interfaceWhite text-textPrimary text-bodylg w-min text-nowrap rounded border px-2 py-1.5 font-medium">
          {conversation?.identifier?.toUpperCase() || ""}
        </div>
      </div>
    );
  };

  const renderProtectTreesDuringConstruction = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Protect trees during construction")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "When you're ready to start work, take steps to protect trees during and after construction. A qualified arborist can advise what precautions you need to take, such as how to set up a Tree Protection Zone (TPZ).",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.boroondara.vic.gov.au/services/planning-and-building/building/works-permits/trees-and-construction/protect-trees-during-construction"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Tree protection instructions")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderGetReadyToApply = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Get ready to apply for a planning permit")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "See what else you might need to do before you apply, what to expect when you lodge, and how your application will be assessed.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.boroondara.vic.gov.au/services/planning-and-building/planning/planning-permits/planning-permit-applications"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Planning permit applications")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  switch (assessmentLights) {
    case AssessmentLights.NoPermitRequired:
      return (
        <>
          {renderPlanningHistory()}
          {renderApplyForWrittenPlanningAdvice()}
          {renderCheckIfYouNeedABuildingPermit()}
          {renderRequestCertificateOfTitle()}
          {renderDiscussWithPlanners()}
        </>
      );
    case AssessmentLights.MoreInformationRequired:
      return (
        <>
          {renderCheckForExistingPermits()}
          {renderProtectTreesDuringConstruction()}
        </>
      );
    case AssessmentLights.RequiresPermit:
      return (
        <>
          {renderRequestCertificateOfTitle()}
          {renderGetReadyToApply()}
          {renderDiscussWithPlanners()}
        </>
      );
    case AssessmentLights.Prohibited:
      return <>{renderDiscussProhibitedWithPlanners()}</>;
  }
}
